import { z } from 'zod';
import { Cents, IsoDateTime, Uuid } from './common.js';

/**
 * Fiches R1 (lots AN et AO, CDC « Fiches R1 ») : CONTRAT GELÉ, toute évolution passe par le PM.
 *
 * - Un R1 est identifié par sa clé `r1Key` = `icalUid` du groupe de copies, sinon `googleEventId` de la copie
 *   principale (`groupCalendarCopies`). Elle ne contient pas la date : un R1 déplacé garde ses infos et sa fiche.
 * - Lot AO : la demande d'infos (`r1_preps`, carte « Compléter le R1 »), le volet R1 de l'agenda, les réglages.
 * - Lot AN : la fiche (`r1_briefs`), les sources, Claude, le mail, les tâches planifiées, Google Places.
 */

export const R1Key = z.string().min(1).max(300);
export type R1Key = z.infer<typeof R1Key>;

// ------------------------------------------------------------------ Réglage `r1Briefs`

const HourMinute = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Heure attendue au format HH:MM' });

export const R1BriefSettings = z.object({
  enabled: z.boolean(),
  /** Jours avant le R1 où la carte « Compléter le R1 » apparaît (tout de suite si le R1 est plus proche). */
  askDaysBefore: z.int().min(1).max(14),
  /** Envoi de la veille (heure de Paris), pour les R1 du lendemain. */
  eveningTime: HourMinute,
  /** Rattrapage du matin (heure de Paris), pour les R1 du jour sans fiche envoyée. */
  morningTime: HourMinute,
  model: z.enum(['claude-opus-5-5', 'claude-sonnet-5-5']),
});
export type R1BriefSettings = z.infer<typeof R1BriefSettings>;

export const DEFAULT_R1_BRIEF_SETTINGS: R1BriefSettings = {
  enabled: true,
  askDaysBefore: 3,
  eveningTime: '18:00',
  morningTime: '07:00',
  model: 'claude-opus-5-5',
};

// ------------------------------------------------------------------ Infos complémentaires (lot AO)

/** Champs de la carte « Compléter le R1 ». Tous facultatifs, chaîne vide = null. */
export const R1PrepFields = z.object({
  company: z.string().max(200).nullable(),
  website: z.string().max(300).nullable(),
  /** Clé de canal (`acquisitionChannels`), ou 'unknown' pour « Je ne sais pas ». */
  channel: z.string().max(80).nullable(),
  /** Recommandé par / apporteur (texte libre). */
  referrer: z.string().max(200).nullable(),
  contactName: z.string().max(200).nullable(),
  contactRole: z.string().max(200).nullable(),
  need: z.string().max(4000).nullable(),
  notes: z.string().max(4000).nullable(),
});
export type R1PrepFields = z.infer<typeof R1PrepFields>;
export const R1_PREP_FIELD_KEYS = Object.keys(R1PrepFields.shape) as (keyof R1PrepFields)[];

/** `PUT /api/r1/:r1Key/prep` : les champs, ou « Rien à ajouter ». */
export const R1PrepInput = z.union([
  z.object({ nothingToAdd: z.literal(true) }),
  z.object({ fields: R1PrepFields.partial() }),
]);
export type R1PrepInput = z.infer<typeof R1PrepInput>;

export const R1Prep = z.object({
  r1Key: R1Key,
  fields: R1PrepFields,
  /** Champs pré-remplis depuis la description de l'agenda (affichés en vert tant qu'ils n'ont pas été modifiés). */
  prefilledKeys: z.array(z.string()),
  nothingToAdd: z.boolean(),
  filledBy: Uuid.nullable(),
  filledAt: IsoDateTime.nullable(),
});
export type R1Prep = z.infer<typeof R1Prep>;

// ------------------------------------------------------------------ Fiche (lot AN)

/**
 * Réponse de Claude (sorties structurées, `z.toJSONSchema(R1BriefContent)`). Pas de min/max dans ce schéma :
 * les limites (5 points de douleur, 3 concurrents…) sont dans la consigne et appliquées après lecture.
 * Les `sourceIds` renvoient à `sources[].id`.
 */
const SourceIds = z.array(z.string());
const Certainty = z.enum(['sure', 'probable', 'unknown']);
const Activity = z.enum(['active', 'inactive', 'unknown']);

export const R1BriefContent = z.object({
  /** Trois phrases au plus. */
  summary: z.string(),
  identification: z.object({
    certainty: Certainty,
    note: z.string().nullable(),
    candidates: z.array(
      z.object({ name: z.string(), siren: z.string().nullable(), city: z.string().nullable() }),
    ),
  }),
  company: z.object({
    legalName: z.string().nullable(),
    siren: z.string().nullable(),
    activity: z.string().nullable(),
    createdOn: z.string().nullable(),
    headcount: z.string().nullable(),
    revenue: z.string().nullable(),
    leaders: z.array(z.object({ name: z.string(), role: z.string().nullable() })),
    procedures: z.string().nullable(),
    sourceIds: SourceIds,
  }),
  contact: z.object({
    name: z.string().nullable(),
    role: z.string().nullable(),
    linkedinUrl: z.string().nullable(),
    linkedinCertainty: Certainty,
  }),
  website: z
    .object({
      url: z.string(),
      platform: z.string().nullable(),
      madeBy: z.string().nullable(),
      copyrightYear: z.string().nullable(),
      mobileScore: z.number().nullable(),
      sourceIds: SourceIds,
    })
    .nullable(),
  domain: z
    .object({ registrar: z.string().nullable(), expiresOn: z.string().nullable(), sourceIds: SourceIds })
    .nullable(),
  google: z
    .object({
      rating: z.number().nullable(),
      reviewCount: z.number().nullable(),
      lastReviewOn: z.string().nullable(),
      hoursUpToDate: z.boolean().nullable(),
      respondsToReviews: z.boolean().nullable(),
      /** Vrai quand la fiche vient de la recherche web et non de Google Places. */
      approximate: z.boolean(),
      sourceIds: SourceIds,
    })
    .nullable(),
  socials: z.array(
    z.object({
      network: z.string(),
      url: z.string().nullable(),
      followers: z.number().nullable(),
      activity: Activity,
      lastPostOn: z.string().nullable(),
      sourceIds: SourceIds,
    }),
  ),
  /** Cinq au plus. */
  pains: z.array(z.object({ text: z.string(), sourceIds: SourceIds })),
  reviews: z.object({ liked: z.array(z.string()), disliked: z.array(z.string()) }).nullable(),
  /** Trois au plus. */
  competitors: z.array(z.object({ name: z.string(), website: z.string().nullable(), note: z.string() })),
  meeting: z.object({
    angle: z.string().nullable(),
    basketEstimate: z.string().nullable(),
    questions: z.array(z.string()),
  }),
  alerts: z.array(z.string()),
  doubts: z.array(z.string()),
  sources: z.array(z.object({ id: z.string(), url: z.string(), title: z.string().nullable() })),
});
export type R1BriefContent = z.infer<typeof R1BriefContent>;

/** « Chez nous » : calculé par le Cockpit depuis sa base, jamais par Claude. */
export const R1BriefInternal = z.object({
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  isCustomer: z.boolean(),
  quotesCount: z.int(),
  emailsCount: z.int(),
  /** Libellé du canal (Axonaut, Cockpit, sinon infos du R1). */
  channelLabel: z.string().nullable(),
  referrer: z.string().nullable(),
  bookedBy: z.string().nullable(),
});
export type R1BriefInternal = z.infer<typeof R1BriefInternal>;

export const R1BriefStatus = z.enum(['PENDING', 'READY', 'ERROR']);
export type R1BriefStatus = z.infer<typeof R1BriefStatus>;

export const R1Brief = z.object({
  r1Key: R1Key,
  status: R1BriefStatus,
  /** Présent quand `status = READY`. */
  content: R1BriefContent.nullable(),
  internal: R1BriefInternal.nullable(),
  error: z.string().nullable(),
  model: z.string().nullable(),
  costCents: Cents.nullable(),
  generatedAt: IsoDateTime.nullable(),
  /** Dernier envoi du mail (null = jamais envoyé). */
  mailedAt: IsoDateTime.nullable(),
  /** Membres à qui le dernier mail est parti. */
  mailedTo: z.array(Uuid),
  /** Vrai si les infos du R1 ont changé depuis la génération (proposer « Régénérer »). */
  stale: z.boolean(),
});
export type R1Brief = z.infer<typeof R1Brief>;

/** `POST /api/r1/:r1Key/brief` : préparer ou régénérer la fiche maintenant. */
export const R1BriefRequest = z.object({ sendMail: z.boolean() });
export type R1BriefRequest = z.infer<typeof R1BriefRequest>;

// ------------------------------------------------------------------ Volet R1 (lot AO, `GET /api/r1/:r1Key`)

export const R1Member = z.object({ id: Uuid, name: z.string() });

/**
 * Lot AX : d'où vient le client d'un R1. `manual` : relié à la main (volet R1 ou « Devis déjà fait », gagne sur
 * le reste) ; `attendee` : un invité dont l'adresse est sur une fiche client ; `posed` : noté par le Cockpit à la
 * pose du rendez-vous (lot AF).
 */
export const R1ClientSource = z.enum(['manual', 'attendee', 'posed']);
export type R1ClientSource = z.infer<typeof R1ClientSource>;

export const R1Detail = z.object({
  r1Key: R1Key,
  title: z.string(),
  startAt: IsoDateTime,
  endAt: IsoDateTime,
  location: z.string().nullable(),
  description: z.string().nullable(),
  clientId: Uuid.nullable(),
  /** Lot AX : nom du client du R1 (ligne « Client : » du volet), null sans client. */
  clientName: z.string().nullable().default(null),
  /** Lot AX : origine du client (null sans client). */
  clientSource: R1ClientSource.nullable().default(null),
  /** Lot AX : quand le client a été relié à la main (null si le lien n'est pas manuel). */
  clientLinkedAt: IsoDateTime.nullable().default(null),
  members: z.array(R1Member),
  prep: R1Prep.nullable(),
  /** Date d'apparition de la carte « Compléter le R1 » (null = pas encore demandée). */
  askedAt: IsoDateTime.nullable(),
  brief: R1Brief.nullable(),
});
export type R1Detail = z.infer<typeof R1Detail>;
